import React, { useMemo, useState } from 'react';
import { Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from '@fluentui/react-components';
import { TCommentIcon } from './EditorCommentsContainer';
import IconButton from './IconButton';
import { EItemScreenMode, useItemScreenStore } from '@modules/items';
import useCommentStore from '@modules/comments/stores/useCommentStore';
import { CommentMultipleFilled, CommentMultipleRegular } from '@fluentui/react-icons';
import { ICON_TOP_OFFSET } from './SingleIcon';

type CombinedIconProps = {
  topPos: number;
  lineCommentIcons: TCommentIcon[];
};

const CombinedIcon: React.FC<CombinedIconProps> = ({ topPos, lineCommentIcons }) => {
  const { setScreenMode } = useItemScreenStore();
  const { setSelectedCommentId, selectedCommentId } = useCommentStore();
  // Local state to track if the dropdown menu is open.
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Memoize the comment IDs for the menu items to determine if any are selected.
  const lineCommentIds: Array<string | null> = useMemo(
    () => lineCommentIcons.map((icon) => icon.id),
    [lineCommentIcons]
  );

  // Toggle the menu open/closed on button click.
  const toggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen((prev) => !prev);
  };

  // Handle selecting a specific comment from the menu.
  const handleSelect = (commentId: string) => {
    setScreenMode(EItemScreenMode.COMMENTS);
    setSelectedCommentId(commentId);
    // Close the menu after selection.
    setIsMenuOpen(false);
  };

  const isSelected = Boolean(selectedCommentId && lineCommentIds.includes(selectedCommentId));
  const Icon = isSelected ? CommentMultipleFilled : CommentMultipleRegular;

  return (
    <div
      className="absolute"
      style={{
        top: `${topPos + ICON_TOP_OFFSET}px`,
      }}
    >
      <Menu
        open={isMenuOpen}
        onOpenChange={(e, data) => {
          e.stopPropagation();
          setIsMenuOpen(data.open);
        }}
        positioning={{ autoSize: true }}
      >
        <MenuTrigger disableButtonEnhancement>
          {/* 
            Why we need to render IconButton this way: 
            https://stackoverflow.com/questions/58493175/cant-get-popover-to-display-in-correct-position-in-dialog
          */}
          {
            IconButton({
              onClick: toggleMenu,
              isSelected,
              Icon,
            }) as any
          }
        </MenuTrigger>

        <MenuPopover>
          <MenuList>
            {lineCommentIcons.map((icon) => (
              <MenuItem
                key={icon.id}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(icon.id);
                }}
              >
                {icon.label}
              </MenuItem>
            ))}
          </MenuList>
        </MenuPopover>
      </Menu>
    </div>
  );
};

export default CombinedIcon;
